import { useState, useMemo } from 'react';
import { demoBookings, demoGuests, demoRooms, demoRoomBeds } from '@/mocks/demoData';
import { useApp } from '@/contexts/AppContext';
import { generateGSTInvoicePDF } from '@/utils/pdfUtils';
import { getPayments as getPaymentsFromStore } from '@/utils/paymentStore';

export default function BookingBilling() {
  const { addToast } = useApp();
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [invoiceType, setInvoiceType] = useState<'final' | 'proforma'>('final');

  const activeBookings = useMemo(() => demoBookings.filter((b) => ['checked_in', 'checked_out', 'confirmed'].includes(b.status)), []);
  const selectedBooking = activeBookings.find((b) => b.id === selectedBookingId);

  const getGuest = (id: string) => demoGuests.find((g) => g.id === id);
  const getRoom = (id: string) => demoRooms.find((r) => r.id === id);

  const handlePrintInvoice = () => {
    if (!selectedBooking) return;
    generateGSTInvoicePDF(
      selectedBooking,
      getGuest(selectedBooking.guest_id),
      getRoom(selectedBooking.room_id),
      getPaymentsFromStore(selectedBooking.id),
      invoiceType
    );
    addToast('success', `${invoiceType === 'final' ? 'GST Invoice' : 'Proforma Invoice'} downloaded as PDF`);
  };

  const handleExportPDF = () => {
    if (!selectedBooking) return;
    handlePrintInvoice();
  };

  const roomTotal = selectedBooking ? Math.ceil((new Date(selectedBooking.check_out).getTime() - new Date(selectedBooking.check_in).getTime()) / (1000 * 60 * 60 * 24)) * (getRoom(selectedBooking.room_id)?.tariff || 0) : 0;
  const extrasTotal = selectedBooking ? selectedBooking.extra_charges.reduce((s, e) => s + e.amount, 0) : 0;
  const subtotal = roomTotal + extrasTotal;
  const cgst = subtotal * 0.06;
  const sgst = subtotal * 0.06;
  const grandTotal = subtotal + cgst + sgst;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">GST Billing</h1>
        <p className="text-navy-500 mt-1">Generate GST-compliant invoices and proforma bills.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left panel */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-navy-100 p-5">
            <h3 className="font-semibold text-navy-800 mb-3">Select Booking</h3>
            <select
              value={selectedBookingId}
              onChange={(e) => setSelectedBookingId(e.target.value)}
              className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 mb-4"
            >
              <option value="">Choose a booking...</option>
              {activeBookings.map((b) => {
                const g = getGuest(b.guest_id);
                const r = getRoom(b.room_id);
                return (
                  <option key={b.id} value={b.id}>
                    {b.booking_id} — {g?.full_name} (Room {r?.room_number})
                  </option>
                );
              })}
            </select>

            <h3 className="font-semibold text-navy-800 mb-3">Invoice Type</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setInvoiceType('final')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${invoiceType === 'final' ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}
              >
                GST Invoice
              </button>
              <button
                onClick={() => setInvoiceType('proforma')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${invoiceType === 'proforma' ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}
              >
                Proforma
              </button>
            </div>
          </div>

          {selectedBooking && (
            <div className="bg-white rounded-xl border border-navy-100 p-5">
              <h3 className="font-semibold text-navy-800 mb-3">Payment History</h3>
              <div className="space-y-2">
                {getPaymentsFromStore(selectedBooking.id).map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-2 bg-navy-50 rounded-lg text-sm">
                    <div>
                      <div className="text-navy-700">{p.receipt_no}</div>
                      <div className="text-xs text-navy-400 capitalize">{p.payment_type} — {p.payment_method}</div>
                    </div>
                    <div className="font-medium text-navy-800">Rs. {p.amount.toLocaleString('en-IN')}</div>
                  </div>
                ))}
                {getPaymentsFromStore(selectedBooking.id).length === 0 && (
                  <div className="text-sm text-navy-400 text-center py-2">No payments recorded</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Invoice Preview */}
        <div className="lg:col-span-2">
          {selectedBooking ? (
            <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
              {/* Invoice Header */}
              <div className="bg-navy-800 text-white p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xl font-bold">Grand Hotel Pro</div>
                    <div className="text-sm text-navy-300 mt-1">123, MG Road, Bengaluru, Karnataka 560001</div>
                    <div className="text-sm text-navy-300">GSTIN: 29AABCU9603R1ZX</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">{invoiceType === 'final' ? 'GST INVOICE' : 'PROFORMA INVOICE'}</div>
                    <div className="text-sm text-navy-300">{selectedBooking.booking_id}</div>
                    <div className="text-sm text-navy-300">{new Date().toLocaleDateString('en-IN')}</div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Guest & Room Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                  <div>
                    <div className="text-xs font-medium text-navy-400 uppercase tracking-wider mb-2">Bill To</div>
                    <div className="font-semibold text-navy-800">{getGuest(selectedBooking.guest_id)?.full_name}</div>
                    <div className="text-sm text-navy-500">{getGuest(selectedBooking.guest_id)?.phone}</div>
                    <div className="text-sm text-navy-500">{getGuest(selectedBooking.guest_id)?.id_proof_type}: {getGuest(selectedBooking.guest_id)?.id_proof_number}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-navy-400 uppercase tracking-wider mb-2">Room Details</div>
                    <div className="font-semibold text-navy-800">Room {getRoom(selectedBooking.room_id)?.room_number}</div>
                    <div className="text-sm text-navy-500">{getRoom(selectedBooking.room_id)?.category}</div>
                    {selectedBooking.is_bed_booking && selectedBooking.bed_ids.length > 0 && (
                      <div className="text-sm text-navy-600 mt-1">
                        <i className="ri-hotel-bed-line mr-1" />
                        Beds: {selectedBooking.bed_ids.map((bid) => {
                          const b = demoRoomBeds.find((x) => x.id === bid);
                          return b ? `Bed ${b.bed_number}` : '';
                        }).filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="text-sm text-navy-500">{new Date(selectedBooking.check_in).toLocaleDateString('en-IN')} to {new Date(selectedBooking.check_out).toLocaleDateString('en-IN')}</div>
                  </div>
                </div>

                {/* Itemized Table */}
                <table className="w-full text-sm mb-6">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold text-navy-700">Description</th>
                      <th className="text-right px-4 py-2.5 font-semibold text-navy-700">Amount (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-navy-100">
                      <td className="px-4 py-3 text-navy-800">
                        Room Charges — {getRoom(selectedBooking.room_id)?.category} Room {getRoom(selectedBooking.room_id)?.room_number}
                        <br />
                        <span className="text-xs text-navy-400">
                          {Math.ceil((new Date(selectedBooking.check_out).getTime() - new Date(selectedBooking.check_in).getTime()) / (1000 * 60 * 60 * 24))} nights x Rs. {getRoom(selectedBooking.room_id)?.tariff.toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-navy-800">{roomTotal.toLocaleString('en-IN')}</td>
                    </tr>
                    {selectedBooking.extra_charges.map((e, i) => (
                      <tr key={i} className="border-t border-navy-100">
                        <td className="px-4 py-3 text-navy-800">{e.name}</td>
                        <td className="px-4 py-3 text-right font-medium text-navy-800">{e.amount.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-navy-200 bg-navy-50">
                      <td className="px-4 py-3 font-semibold text-navy-700">Subtotal</td>
                      <td className="px-4 py-3 text-right font-semibold text-navy-800">{subtotal.toLocaleString('en-IN')}</td>
                    </tr>
                    <tr className="border-t border-navy-100">
                      <td className="px-4 py-2 text-navy-600">CGST @ 6%</td>
                      <td className="px-4 py-2 text-right text-navy-700">{cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr className="border-t border-navy-100">
                      <td className="px-4 py-2 text-navy-600">SGST @ 6%</td>
                      <td className="px-4 py-2 text-right text-navy-700">{sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr className="border-t-2 border-navy-200">
                      <td className="px-4 py-3 font-bold text-navy-800 text-base">Grand Total</td>
                      <td className="px-4 py-3 text-right font-bold text-navy-800 text-base">Rs. {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>

                {invoiceType === 'final' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                    <div className="flex items-center justify-between text-sm mb-3">
                      <div>
                        <div className="font-medium text-green-800">Amount Paid</div>
                        <div className="text-green-600">Rs. {(grandTotal - selectedBooking.pending_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-red-800">Amount Pending</div>
                        <div className="text-red-600">Rs. {selectedBooking.pending_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>
                    {selectedBooking.deposit > 0 && (
                      <div className="border-t border-green-200 pt-3">
                        <div className="text-sm text-green-700">
                          <i className="ri-secure-payment-line mr-1" />
                          <span className="font-medium">Advance Deposit:</span> Rs. {selectedBooking.deposit.toLocaleString('en-IN')}
                          <span className="text-xs text-green-500 ml-2">(adjusted in final bill)</span>
                        </div>
                      </div>
                    )}
                    {getPaymentsFromStore(selectedBooking.id).length > 0 && (
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <div className="text-xs font-medium text-green-700 mb-1">Payment Breakdown:</div>
                        {getPaymentsFromStore(selectedBooking.id).map((p) => (
                          <div key={p.id} className="flex justify-between text-xs text-green-600">
                            <span className="capitalize">{p.receipt_no} — {p.payment_method}</span>
                            <span>Rs. {p.amount.toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handlePrintInvoice}
                    className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-file-download-line mr-2" /> Download PDF Invoice
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-file-pdf-line mr-2" /> Export PDF
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
              <i className="ri-bill-line text-5xl text-navy-200 mb-4 block" />
              <div className="text-lg font-medium text-navy-600 mb-1">No Invoice Selected</div>
              <p className="text-sm text-navy-400">Select a booking from the left panel to generate an invoice.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}